import React, { createContext, useEffect, useState, useCallback, useContext } from "react";
import { LogContext } from './LogContext';

declare global {
    interface Window {
        ReactNativeWebView?: {
            postMessage: (message: string) => void;
        };
    }
}

interface Message {
    action?: string;
    type?: string;
    payload?: any;
    success?: boolean;
    error?: string;
    productId?: string;
    customerInfo?: any;
    offerings?: any;
    message?: string;
    data?: any;
    userId?: string;
    userProfile?: {
        email?: string;
        firstName?: string;
        lastName?: string;
    };
    level?: 'info' | 'warn' | 'error' | 'debug'; // For RC_LOG messages
}

interface RNBridgeContextType {
    lastMessage: Message | null;
    sendMessage: (msg: Message) => void;
}

export const RNBridgeContext = createContext<RNBridgeContextType>({
    lastMessage: null,
    sendMessage: () => { },
});

export const RNBridgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [lastMessage, setLastMessage] = useState<Message | null>(null);

    // Get log function from context if available (don't throw if not available)
    const logContext = useContext(LogContext);
    const log = logContext?.log || null;

    // Listen to messages from React Native
    useEffect(() => {
        const handler = (event: MessageEvent | Event) => {
            try {
                const data = JSON.parse((event as MessageEvent).data);

                setLastMessage(data);

                // Automatically pipe RC_LOG messages to LogContext
                if (data.type === 'RC_LOG' && log) {
                    const level = data.level || 'info';
                    log(level as any, data.message || 'RevenueCat log', data.data);
                }

                // Also log RC_CONFIG messages
                if (data.type === 'RC_CONFIG' && log) {
                    log('info', data.message || 'RevenueCat Config', data.data);
                }
            } catch (e) {
                //  console.warn("Invalid RN message", (event as MessageEvent).data);
            }
        };

        window.addEventListener("message", handler as EventListener);
        document.addEventListener("message", handler as EventListener); // Android fallback

        return () => {
            window.removeEventListener("message", handler as EventListener);
            document.removeEventListener("message", handler as EventListener);
        };
    }, [log]);


    const sendMessage = useCallback((msg: Message) => {
        console.log('[RNBridge] Sending message to React Native:', msg);
        console.log('[RNBridge] ReactNativeWebView available:', !!window.ReactNativeWebView);
        const serialized = JSON.stringify(msg);
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(serialized);
            console.log('[RNBridge] Message sent successfully');
        } else {
            console.warn('[RNBridge] ReactNativeWebView not available - message not sent');
        }
    }, []);

    return (
        <RNBridgeContext.Provider value={{ lastMessage, sendMessage }}>
            {children}
        </RNBridgeContext.Provider>
    );
};
